import { supabase } from '@/lib/supabase';
import type {
  Project,
  ProjectWithCustomer,
  CreateProjectInput,
  UpdateProjectInput,
  ProjectStatus,
} from '../types';

/**
 * Fetch all projects for a business using the v_projects_with_customer view
 */
export async function fetchProjects(
  businessId: string
): Promise<ProjectWithCustomer[]> {
  const { data, error } = await supabase
    .from('v_projects_with_customer')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[projectsApi] Error fetching projects:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Fetch projects with filters
 */
export async function fetchProjectsWithFilters(
  businessId: string,
  filters: {
    status?: ProjectStatus;
    customerId?: string;
    search?: string;
  },
  sorting: {
    field: 'created_at' | 'start_date' | 'customer_name';
    direction: 'asc' | 'desc';
  } = { field: 'created_at', direction: 'desc' }
): Promise<ProjectWithCustomer[]> {
  let query = supabase
    .from('v_projects_with_customer')
    .select('*')
    .eq('business_id', businessId);

  // Apply filters
  if (filters.status) {
    query = query.eq('status', filters.status);
  }

  if (filters.customerId) {
    query = query.eq('customer_id', filters.customerId);
  }

  if (filters.search) {
    query = query.or(
      `project_name.ilike.%${filters.search}%,customer_name.ilike.%${filters.search}%,site_street_address.ilike.%${filters.search}%,site_city.ilike.%${filters.search}%,customer_billing_street_address.ilike.%${filters.search}%,customer_billing_city.ilike.%${filters.search}%`
    );
  }

  // Apply sorting
  query = query.order(sorting.field, { ascending: sorting.direction === 'asc' });

  const { data, error } = await query;

  if (error) {
    console.error('[projectsApi] Error fetching projects with filters:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Fetch a single project by ID using the v_projects_with_customer view
 */
export async function fetchProjectById(
  projectId: string
): Promise<ProjectWithCustomer | null> {
  const { data, error } = await supabase
    .from('v_projects_with_customer')
    .select('*')
    .eq('id', projectId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }
    console.error('[projectsApi] Error fetching project:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create a new project
 */
export async function createProject(input: CreateProjectInput): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .insert({
      business_id: input.business_id,
      customer_id: input.customer_id,
      project_name: input.project_name,
      status: input.status || 'planned',
      start_date: input.start_date || null,
      end_date: input.end_date || null,
    })
    .select()
    .single();

  if (error) {
    console.error('[projectsApi] Error creating project:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update an existing project
 */
export async function updateProject(
  projectId: string,
  input: UpdateProjectInput
): Promise<Project> {
  const { data, error } = await supabase
    .from('projects')
    .update(input)
    .eq('id', projectId)
    .select()
    .single();

  if (error) {
    console.error('[projectsApi] Error updating project:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Delete a project
 */
export async function deleteProject(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('projects')
    .delete()
    .eq('id', projectId);

  if (error) {
    console.error('[projectsApi] Error deleting project:', error);
    throw new Error(error.message);
  }
}

/**
 * Get project counts by status for a business
 */
export async function fetchProjectCountsByStatus(
  businessId: string
): Promise<Record<ProjectStatus, number>> {
  const { data, error } = await supabase
    .from('projects')
    .select('status')
    .eq('business_id', businessId);

  if (error) {
    console.error('[projectsApi] Error fetching project counts:', error);
    throw new Error(error.message);
  }

  // Count projects by status
  const counts: Record<ProjectStatus, number> = {
    planned: 0,
    in_progress: 0,
    completed: 0,
    cancelled: 0,
  };

  data?.forEach((project) => {
    const status = project.status as ProjectStatus;
    if (status in counts) {
      counts[status]++;
    }
  });

  return counts;
}